// Helpers for sections an owner adds on top of a designed template.
/** Index just after the closing tag of the element whose opening tag starts at `start` (nesting of the same tag name is respected). */
export function elementEnd(html, start, tag) {
    const re = new RegExp('<(/?)' + tag + '(?=[\\s>/])[^>]*>', 'gi');
    re.lastIndex = start;
    let depth = 0;
    let m;
    while ((m = re.exec(html))) {
        if (m[1] === '/') {
            depth -= 1;
            if (depth === 0)
                return m.index + m[0].length;
        }
        else if (!m[0].endsWith('/>')) {
            depth += 1;
        }
    }
    return -1;
}
/**
 * Puts each owner-added section directly after the design section it belongs to.
 * If that section cannot be found the addition goes just before </body>, so it is never lost.
 */
export function insertExtras(html, extras) {
    const inserts = [];
    extras.forEach((extra, seq) => {
        const open = new RegExp('<([a-zA-Z][a-zA-Z0-9-]*)(?=[^>]*\\sdata-sec="' + extra.after + '")[^>]*>');
        const m = open.exec(html);
        let at = -1;
        if (m)
            at = elementEnd(html, m.index, m[1]);
        if (at < 0)
            at = html.search(/<\/body>/i);
        if (at < 0)
            at = html.length;
        inserts.push({ at, html: extra.html, seq });
    });
    // Insert from the back so earlier positions stay valid; same-anchor additions keep their list order.
    inserts.sort((a, b) => b.at - a.at || b.seq - a.seq);
    let out = html;
    for (const ins of inserts)
        out = out.slice(0, ins.at) + ins.html + out.slice(ins.at);
    return out;
}
function clamp(n) {
    return Math.max(0, Math.min(255, Math.round(n)));
}
/** Any css colour the design declares (#rgb, #rrggbb, rgb(), rgba(), hsl()) as #rrggbb, or null. */
export function toHex(value) {
    const v = value.trim().toLowerCase();
    let m = /^#([0-9a-f]{3})$/.exec(v);
    if (m)
        return '#' + m[1].split('').map((c) => c + c).join('');
    m = /^#([0-9a-f]{6})(?:[0-9a-f]{2})?$/.exec(v);
    if (m)
        return '#' + m[1];
    m = /^rgba?\(\s*([\d.]+)[ ,]+([\d.]+)[ ,]+([\d.]+)/.exec(v);
    if (m)
        return '#' + [m[1], m[2], m[3]].map((x) => clamp(Number(x)).toString(16).padStart(2, '0')).join('');
    m = /^hsla?\(\s*([\d.]+)(?:deg)?[ ,]+([\d.]+)%[ ,]+([\d.]+)%/.exec(v);
    if (m) {
        const h = Number(m[1]) / 360;
        const s = Number(m[2]) / 100;
        const l = Number(m[3]) / 100;
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        const f = (t) => {
            const tt = t < 0 ? t + 1 : t > 1 ? t - 1 : t;
            if (tt < 1 / 6)
                return p + (q - p) * 6 * tt;
            if (tt < 1 / 2)
                return q;
            if (tt < 2 / 3)
                return p + (q - p) * (2 / 3 - tt) * 6;
            return p;
        };
        return '#' + [f(h + 1 / 3), f(h), f(h - 1 / 3)].map((x) => clamp(x * 255).toString(16).padStart(2, '0')).join('');
    }
    return null;
}
function lum(hex) {
    const ch = (i) => {
        const v = parseInt(hex.slice(1 + i * 2, 3 + i * 2), 16) / 255;
        return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * ch(0) + 0.7152 * ch(1) + 0.0722 * ch(2);
}
export function contrast(a, b) {
    const [hi, lo] = [lum(a), lum(b)].sort((x, y) => y - x);
    return (hi + 0.05) / (lo + 0.05);
}
function mix(a, b, amountOfB) {
    const c = (i) => {
        const x = parseInt(a.slice(1 + i * 2, 3 + i * 2), 16);
        const y = parseInt(b.slice(1 + i * 2, 3 + i * 2), 16);
        return clamp(x + (y - x) * amountOfB).toString(16).padStart(2, '0');
    };
    return '#' + c(0) + c(1) + c(2);
}
/** Hue, saturation and lightness (0 to 1) of a #rrggbb colour. */
function hsl(hex) {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    const d = max - min;
    const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
    return { s, l };
}
/**
 * For templates whose brand colour is not called accent/primary (for example --green-700): the most saturated colour that stands out
 * from the background, preferring a mid-weight shade so buttons stay readable.
 */
function brandColour(vars, background) {
    const bright = lum(background) > 0.4;
    const skip = /(bg|background|surface|border|line|ink|text|muted|danger|error|shadow|glow|focus|white|black|overlay)/i;
    const candidates = vars.filter((v) => !skip.test(v.name) && hsl(v.hex).s >= 0.3 && contrast(v.hex, background) >= 3);
    if (candidates.length === 0)
        return null;
    const target = bright ? 0.3 : 0.65;
    candidates.sort((a, b) => Math.abs(hsl(a.hex).l - target) - Math.abs(hsl(b.hex).l - target));
    return candidates[0].hex;
}
/** The slightly darker panel a "soft tint" section sits on. */
export function tintOf(background, accent) {
    return mix(background, accent, 0.08);
}
/** Colours for content that sits on the brand colour itself, each checked against the button text colour. */
export function onAccentColours(accent, accentText) {
    const toward = accentText === '#ffffff' || lum(accentText) > 0.5 ? '#000000' : '#ffffff';
    let amount = 0.18;
    let surface = mix(accent, toward, amount);
    while (contrast(accentText, surface) < 4.5 && amount < 0.7) {
        amount += 0.08;
        surface = mix(accent, toward, amount);
    }
    let muted = mix(accentText, accent, 0.16);
    if (contrast(muted, accent) < 4.5 || contrast(muted, surface) < 4.5)
        muted = accentText;
    return { surface, muted, border: mix(accent, accentText, 0.3) };
}
/**
 * Picks a readable palette for added sections from the template's own colour variables (with the owner's overrides applied).
 * The names designs use vary, so this goes by name first and falls back to safe black/white choices.
 * Every text colour is checked against every background an added section can have (page, panel, tint, brand colour).
 */
export function extrasPalette(vars, overrides = {}) {
    const resolved = vars
        .map((v) => ({ name: v.name, hex: toHex(overrides[v.name] ?? v.value) }))
        .filter((v) => v.hex !== null);
    const find = (re, avoid) => resolved.find((v) => re.test(v.name) && !(avoid && avoid.test(v.name)))?.hex ?? null;
    const background = find(/^--(bg|background|paper|canvas|cream|base|page)$/i) ?? find(/(^|-)(bg|background|paper|canvas|cream)(-|$)/i, /(dark|alt|2|3)/i) ?? '#ffffff';
    const bright = lum(background) > 0.4;
    const onBg = bright ? '#111111' : '#f5f5f5';
    const away = bright ? '#000000' : '#ffffff';
    let text = find(/^--(ink|text|fg|foreground|body|on-bg)$/i) ?? find(/(^|-)(ink|text|fg|foreground)(-|$)/i, /(muted|soft|light|dim|on-accent|inverse)/i) ?? onBg;
    if (contrast(text, background) < 4.5)
        text = onBg;
    let accent = find(/^--(accent|primary|brand|cta)$/i) ?? find(/(accent|primary|brand|cta)/i, /(text|soft|light|dark|contrast)/i) ?? brandColour(resolved, background) ?? '#2563eb';
    // Links and outlines use the brand colour as text: it must read on the page and on the soft tint.
    for (let i = 0; i < 12 && (contrast(accent, background) < 4.5 || contrast(accent, tintOf(background, accent)) < 4.5); i += 1) {
        accent = mix(accent, away, 0.1);
    }
    const pickText = (c) => (contrast('#ffffff', c) >= contrast('#111111', c) ? '#ffffff' : '#111111');
    let accentText = pickText(accent);
    // A mid-tone brand colour can fail with both black and white text: nudge it until button text is readable.
    for (let i = 0; i < 10 && contrast(accentText, accent) < 4.5; i += 1) {
        accent = mix(accent, accentText === '#ffffff' ? '#000000' : '#ffffff', 0.1);
        accentText = pickText(accent);
    }
    const tint = tintOf(background, accent);
    let surface = find(/^--(surface|card|panel|elevated)$/i) ?? find(/(surface|card|panel|elev)/i, /(alt|dark|border)/i) ?? mix(background, text, 0.06);
    if (contrast(text, surface) < 4.5)
        surface = mix(background, text, 0.06);
    let muted = find(/^--(muted|soft|dim|subtle)$/i) ?? find(/(muted|soft|dim|subtle|sub)/i, /(bg|background)/i) ?? mix(text, background, 0.4);
    for (let i = 0; i < 12 && [background, surface, tint].some((bg) => contrast(muted, bg) < 4.5); i += 1) {
        muted = mix(muted, text, 0.2);
    }
    const border = find(/^--(border|line|rule|hair|stroke)$/i) ?? mix(background, text, 0.16);
    return { background, surface, tint, text, heading: text, accent, accentText, muted, border, onAccent: onAccentColours(accent, accentText) };
}
//# sourceMappingURL=design-extras.js.map